import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { OpenInNewOutlined, VideocamOutlined } from '@mui/icons-material';
import type { SafetyEvidenceCardPayload } from '../../services/reportsApi';
import { api } from '../../services/api';

function text(value?: string | null): string {
  const normalized = String(value || '').trim();
  return normalized || 'Not available';
}

function formatDateTime(value?: string | null): string {
  const raw = String(value || '').trim();
  if (!raw) return 'Not available';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatTokenLabel(value?: string | null): string {
  const raw = String(value || '').trim();
  if (!raw) return 'Not available';
  return raw
    .replace(/zone[_\s-]?violation/gi, 'restricted-zone exposure')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function resolveRenderedImageDataUrl(card: SafetyEvidenceCardPayload): string | null {
  const direct = String(card.rendered_image_data_url || '').trim();
  if (direct.startsWith('data:')) return direct;

  const base64 = String(card.rendered_image_base64 || '').trim();
  if (!base64) return null;

  const mime = String(card.rendered_image_mime_type || 'image/jpeg').trim() || 'image/jpeg';
  return `data:${mime};base64,${base64}`;
}

function resolveSnapshotUri(card: SafetyEvidenceCardPayload): string {
  return String(
    card.snapshot_uri
      || card.snapshot_path
      || card.media_uri
      || '',
  ).trim();
}

function resolveClipUri(card: SafetyEvidenceCardPayload): string {
  return String(card.clip_path || '').trim();
}

function resolveSecondaryEvidenceUri(card: SafetyEvidenceCardPayload): string {
  return String(card.secondary_snapshot_uri || card.secondary_clip_uri || '').trim();
}

function evidenceQualityBadgeProps(status?: string | null): {
  label: string;
  color: 'default' | 'success' | 'warning' | 'info' | 'error';
  variant: 'filled' | 'outlined';
} {
  const normalized = String(status || '').trim().toLowerCase();
  const label = formatTokenLabel(status);

  switch (normalized) {
    case 'visual_evidence_verified':
      return { label, color: 'success', variant: 'filled' };
    case 'demo_evidence':
      return { label, color: 'warning', variant: 'filled' };
    case 'metadata_only':
      return { label, color: 'warning', variant: 'outlined' };
    case 'representative_sample':
      return { label, color: 'info', variant: 'outlined' };
    case 'appendix_only':
      return { label, color: 'default', variant: 'outlined' };
    default:
      return { label, color: 'default', variant: 'outlined' };
  }
}

function severityChipColor(value?: string | null): 'default' | 'error' | 'warning' | 'info' {
  const normalized = String(value || '').trim().toLowerCase();
  if (/sif|severe|critical|high/.test(normalized)) return 'error';
  if (/moderate|medium/.test(normalized)) return 'warning';
  if (/low|minor/.test(normalized)) return 'info';
  return 'default';
}

async function fetchBlobUrl(uri: string): Promise<string> {
  const response = await api.get(uri, { responseType: 'blob' });
  return URL.createObjectURL(response.data as Blob);
}

export function SafetyEvidenceCard(props: {
  card: SafetyEvidenceCardPayload;
  index?: number;
  buyerFacing?: boolean;
}) {
  const card = props.card;
  const buyerFacing = Boolean(props.buyerFacing);
  const cardId = String(card.evidence_id || card.incident_id || props.index || 'evidence-card');
  const renderedDataUrl = useMemo(() => resolveRenderedImageDataUrl(card), [card]);
  const snapshotUri = useMemo(() => resolveSnapshotUri(card), [card]);
  const clipUri = useMemo(() => resolveClipUri(card), [card]);
  const secondaryEvidenceUri = useMemo(() => resolveSecondaryEvidenceUri(card), [card]);
  const qualityBadge = evidenceQualityBadgeProps(card.evidence_quality_status);
  const primaryImageLabel = String(card.primary_image_label || '').trim();
  const secondaryEvidenceLabel = String(card.secondary_evidence_label || '').trim();
  const bboxStatusLabel = String(card.bbox_status_label || '').trim();
  const incidentSubjectLabel = String(card.incident_subject_label || '').trim();
  const triggerVerificationLabel = String(card.trigger_person_verification_label || '').trim();
  const signedMetadata = [
    card.camera_id ? `Camera ${card.camera_id}` : '',
    card.zone_id ? `Zone ${card.zone_id}` : '',
    card.risk_type ? `Type ${formatTokenLabel(card.risk_type)}` : '',
    card.incident_id ? `Incident ${card.incident_id}` : '',
  ].filter(Boolean);
  const revisionBoundUnavailable = String(
    (card as SafetyEvidenceCardPayload & { revision_bound_media?: string | null })
      .revision_bound_media || '',
  ).trim().toLowerCase() === 'unavailable';
  const mediaNote = String(
    (card as SafetyEvidenceCardPayload & { media_note?: string | null }).media_note || '',
  ).trim();

  const [imageUrl, setImageUrl] = useState(renderedDataUrl || '');
  const [clipUrl, setClipUrl] = useState('');
  const [secondaryEvidenceUrl, setSecondaryEvidenceUrl] = useState('');
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const [clipLoadFailed, setClipLoadFailed] = useState(false);
  const [secondaryEvidenceLoadFailed, setSecondaryEvidenceLoadFailed] = useState(false);

  useEffect(() => {
    if (renderedDataUrl) {
      setImageUrl(renderedDataUrl);
      setImageLoadFailed(false);
      return undefined;
    }

    let cancelled = false;
    const objectUrls: string[] = [];

    async function loadSnapshot() {
      if (!snapshotUri) {
        setImageUrl('');
        setImageLoadFailed(true);
        return;
      }
      setImageLoadFailed(false);
      try {
        const nextUrl = await fetchBlobUrl(snapshotUri);
        objectUrls.push(nextUrl);
        if (!cancelled) setImageUrl(nextUrl);
      } catch {
        if (!cancelled) {
          setImageUrl('');
          setImageLoadFailed(true);
        }
      }
    }

    void loadSnapshot();

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch {
          // ignore blob cleanup failures
        }
      });
    };
  }, [renderedDataUrl, snapshotUri]);

  useEffect(() => {
    let cancelled = false;
    const objectUrls: string[] = [];

    async function loadClip() {
      if (!clipUri) {
        setClipUrl('');
        setClipLoadFailed(false);
        return;
      }
      setClipLoadFailed(false);
      try {
        const nextUrl = clipUri.startsWith('blob:') || clipUri.startsWith('http')
          ? clipUri
          : await fetchBlobUrl(clipUri);
        if (nextUrl.startsWith('blob:')) objectUrls.push(nextUrl);
        if (!cancelled) setClipUrl(nextUrl);
      } catch {
        if (!cancelled) {
          setClipUrl('');
          setClipLoadFailed(true);
        }
      }
    }

    void loadClip();

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch {
          // ignore blob cleanup failures
        }
      });
    };
  }, [clipUri]);

  useEffect(() => {
    let cancelled = false;
    const objectUrls: string[] = [];

    async function loadSecondaryEvidence() {
      if (!secondaryEvidenceUri) {
        setSecondaryEvidenceUrl('');
        setSecondaryEvidenceLoadFailed(false);
        return;
      }
      setSecondaryEvidenceLoadFailed(false);
      try {
        const nextUrl = secondaryEvidenceUri.startsWith('blob:') || secondaryEvidenceUri.startsWith('http')
          ? secondaryEvidenceUri
          : await fetchBlobUrl(secondaryEvidenceUri);
        if (nextUrl.startsWith('blob:')) objectUrls.push(nextUrl);
        if (!cancelled) setSecondaryEvidenceUrl(nextUrl);
      } catch {
        if (!cancelled) {
          setSecondaryEvidenceUrl('');
          setSecondaryEvidenceLoadFailed(true);
        }
      }
    }

    void loadSecondaryEvidence();

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch {
          // ignore blob cleanup failures
        }
      });
    };
  }, [secondaryEvidenceUri]);

  return (
    <Card
      variant="outlined"
      sx={{ borderRadius: 3, height: '100%', boxShadow: 'none' }}
      data-testid={`safety-evidence-card-${cardId}`}
    >
      <Box sx={{ position: 'relative', aspectRatio: '16 / 9', bgcolor: '#0f172a' }}>
        {!imageLoadFailed && imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt={text(card.safety_rule)}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <Stack
            spacing={1}
            alignItems="center"
            justifyContent="center"
            sx={{ height: '100%', color: 'rgba(255,255,255,0.82)', px: 2, textAlign: 'center' }}
          >
            <Typography variant="subtitle2">Snapshot unavailable</Typography>
            <Typography variant="caption">
              Visual evidence could not be loaded for this card.
            </Typography>
          </Stack>
        )}
        {buyerFacing ? null : (
          <Chip
            label={qualityBadge.label}
            size="small"
            color={qualityBadge.color}
            variant={qualityBadge.variant}
            sx={{ position: 'absolute', top: 10, left: 10 }}
          />
        )}
        {!buyerFacing && card.is_anchor ? (
          <Chip
            label="Anchor"
            size="small"
            sx={{ position: 'absolute', top: 10, right: 10, bgcolor: 'rgba(255,255,255,0.92)' }}
          />
        ) : null}
      </Box>
      <CardContent>
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
            <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: '1 1 auto' }}>
              {text(card.safety_rule)}
            </Typography>
            {buyerFacing ? null : (
              <Chip
                size="small"
                color={severityChipColor(card.risk_level)}
                label={formatTokenLabel(card.risk_level)}
              />
            )}
          </Stack>
          {buyerFacing ? null : (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {primaryImageLabel ? (
                <Chip size="small" variant="outlined" color="info" label={primaryImageLabel} />
              ) : null}
              {secondaryEvidenceLabel ? (
                <Chip size="small" variant="outlined" label={secondaryEvidenceLabel} />
              ) : null}
              {incidentSubjectLabel ? (
                <Chip size="small" variant="outlined" label={`Incident subject: ${incidentSubjectLabel}`} />
              ) : null}
            </Stack>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {formatDateTime(card.timestamp)}
          </Typography>
          {!buyerFacing && bboxStatusLabel ? (
            <Typography variant="caption" color="text.secondary">
              {bboxStatusLabel}
            </Typography>
          ) : null}
          {!buyerFacing && triggerVerificationLabel ? (
            <Typography variant="caption" color="text.secondary">
              {triggerVerificationLabel}
            </Typography>
          ) : null}
          {text(card.area) !== 'Not available' ? (
            <Typography variant="body2" color="text.secondary">
              {text(card.area)}
            </Typography>
          ) : null}
          {!buyerFacing && signedMetadata.length ? (
            <Typography variant="caption" color="text.secondary">
              {signedMetadata.join(' · ')}
            </Typography>
          ) : null}
          {!buyerFacing && card.lighting?.label ? (
            <Typography variant="body2" data-testid="evidence-card-lighting">
              <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Lighting
              </Typography>
              {String(card.lighting.label)}
              {card.lighting.confidence ? (
                <Typography component="span" variant="caption" color="text.secondary">
                  {` · ${String(card.lighting.confidence)} confidence`}
                </Typography>
              ) : null}
            </Typography>
          ) : null}
          <Typography variant="body2">
            <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Recommended action
            </Typography>
            {text(card.recommended_action)}
          </Typography>
          {!buyerFacing && revisionBoundUnavailable ? (
            <Typography variant="caption" color="text.secondary" data-testid="revision-bound-media-note">
              {mediaNote || 'Revision-bound media is unavailable for this signed card.'}
            </Typography>
          ) : null}
          {!buyerFacing && !revisionBoundUnavailable && snapshotUri && imageUrl && !imageLoadFailed ? (
            <Button
              variant="outlined"
              size="small"
              endIcon={<OpenInNewOutlined fontSize="small" />}
              href={imageUrl}
              target="_blank"
              rel="noreferrer"
              aria-label={`Open snapshot for ${text(card.safety_rule)}`}
            >
              Open snapshot
            </Button>
          ) : null}
          {!buyerFacing && clipUri ? (
            clipUrl && !clipLoadFailed ? (
              <Button
                variant="outlined"
                size="small"
                startIcon={<VideocamOutlined />}
                endIcon={<OpenInNewOutlined fontSize="small" />}
                href={clipUrl}
                target="_blank"
                rel="noreferrer"
                aria-label={`Open clip for ${text(card.safety_rule)}`}
              >
                Open clip
              </Button>
            ) : (
              <Chip label="Clip unavailable" size="small" variant="outlined" />
            )
          ) : null}
          {!buyerFacing && secondaryEvidenceUri ? (
            secondaryEvidenceUrl && !secondaryEvidenceLoadFailed ? (
              <Button
                variant="text"
                size="small"
                endIcon={<OpenInNewOutlined fontSize="small" />}
                href={secondaryEvidenceUrl}
                target="_blank"
                rel="noreferrer"
                aria-label={`Open raw source evidence for ${text(card.safety_rule)}`}
              >
                Open raw source evidence
              </Button>
            ) : (
              <Chip label="Raw source evidence retained" size="small" variant="outlined" />
            )
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}

export function SafetyEvidenceCardsSection(props: {
  cards?: SafetyEvidenceCardPayload[] | null;
  title?: string;
  subtitle?: string;
  buyerFacing?: boolean;
}) {
  const cards = Array.isArray(props.cards) ? props.cards.filter(Boolean) : [];
  if (!cards.length) return null;

  return (
    <Box sx={{ mb: 3 }} data-testid="safety-evidence-cards-section">
      <Stack spacing={1} sx={{ mb: 2 }}>
        <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
          {props.title || 'Safety evidence cards'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {props.subtitle || 'Representative visual evidence aligned to the signed safety scan findings.'}
        </Typography>
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' },
        }}
      >
        {cards.map((card, index) => (
          <SafetyEvidenceCard
            key={String(card.evidence_id || card.incident_id || index)}
            card={card}
            index={index}
            buyerFacing={props.buyerFacing}
          />
        ))}
      </Box>
    </Box>
  );
}
